import { api } from '../../api-helper';
import { URLS } from '../../../constants/URLS';

/**
 * Control Center — Document Category Mapping (TOW-207).
 *
 * A document type holds at most one category, so the mapping is a single
 * nullable id going in and a single nullable category object coming back —
 * never an array. The category list itself is read through
 * `getAttachmentTypeCategories` (modules/order/orderDocumnet.js), which the
 * order Documents filter already uses.
 *
 * Full contract: docs/document-category-mapping-api.md
 */

export const createAttachmentTypeCategory = (category) => {
  return api.post(
    URLS.attachmentTypeCategory,
    { category },
    {
      headers: {
        needsToken: true
      }
    }
  );
};

export const updateAttachmentTypeCategory = ({ id, category }) => {
  return api.put(
    URLS.attachmentTypeCategory,
    { id, category },
    {
      headers: {
        needsToken: true
      }
    }
  );
};

export const deleteAttachmentTypeCategory = (categoryId) => {
  return api.delete(URLS.attachmentTypeCategory, {
    params: { id: categoryId },
    headers: {
      needsToken: true
    }
  });
};

export const getDocumentTypeCategories = ({
  page = 1,
  limit = 20,
  query = null,
  categoryIds = [],
  unmapped = false
}) => {
  return api.get(URLS.documentTypeCategories, {
    params: {
      page,
      limit,
      ...(query && { query }),
      ...(categoryIds?.length && { categoryIds: categoryIds.join(',') }),
      ...(unmapped && { unmapped: true })
    },
    headers: {
      needsToken: true
    }
  });
};

/**
 * Updates a document type's mapping. `categoryId` of null clears the category
 * assignment, `eventCode` of null clears the Resware event code, and
 * `reswareDocumentId` of null clears the Resware document ID. Only
 * the keys passed in are sent, so editing one field can't wipe another by
 * omitting it.
 */
export const updateDocumentTypeCategory = ({
  documentTypeId,
  categoryId,
  eventCode,
  reswareDocumentId
}) => {
  return api.put(
    URLS.documentTypeCategory,
    {
      documentTypeId,
      ...(categoryId !== undefined && { categoryId }),
      ...(eventCode !== undefined && { eventCode }),
      ...(reswareDocumentId !== undefined && { reswareDocumentId })
    },
    {
      headers: {
        needsToken: true
      }
    }
  );
};

/**
 * Creates a new document type from the Document Type Management hub. Category
 * and per-profile-type permissions are optional and only sent when given; the
 * Resware document ID and event code are always sent, as null when left empty.
 */
export const createDocumentType = ({
  name,
  categoryId,
  reswareDocumentId,
  eventCode,
  profilePermissions
}) => {
  return api.post(
    URLS.documentsTypes,
    {
      name,
      ...(categoryId && { categoryId }),
      reswareDocumentId: reswareDocumentId ?? null,
      eventCode: eventCode || null,
      ...(profilePermissions?.length && { profilePermissions })
    },
    {
      headers: {
        needsToken: true
      }
    }
  );
};

export const deleteDocumentType = (documentTypeId) => {
  return api.delete(URLS.documentsTypes, {
    params: { id: documentTypeId },
    headers: {
      needsToken: true
    }
  });
};
